import React, {
  memo,
  useCallback,
  useMemo,
} from 'react';

import ReactLinkify from 'react-linkify';

import { useTheme } from '../../context/ThemeProvider';
import {
  useAppNavigate,
  useAppSelector,
} from '../../shared/hooks';
import { ROUTES } from '../../types/routes';

const regex = /{{[0-9a-f]{24}#[\s\S]*?}}/g;

interface Props {
  content: string;
  showPlainText?: boolean;
  disableMentionClick?: boolean;
}

const MentionedContent = ({
  content,
  showPlainText,
  disableMentionClick,
}: Props) => {
  const navigate = useAppNavigate();

  const { colors: COLORS } = useTheme();

  const { isTagMango } = useAppSelector((state) => state.app);

  const onClickMention = useCallback(
    (mentionedUserId: string) => {
      if (!isTagMango) {
        navigate(ROUTES.USER_PROFILE, { userId: mentionedUserId });
      } else {
        window.open(
          ROUTES.CREATOR_PROFILE.replace(':id', mentionedUserId),
          '_blank',
        );
      }
    },
    [isTagMango, navigate],
  );

  const getNameFromMentiontag = (tag: string) =>
    tag.substring(tag.indexOf('#') + 1, tag.length - 2);

  const getIdFromMentiontag = (tag: string) =>
    tag.substring(2, tag.indexOf('#'));

  const textToRender = useMemo(() => {
    const text = content;
    const tagsRegexResult = `${text}`.match(regex);
    const tags: string[] = [];

    tagsRegexResult?.forEach((element) => {
      tags.push(element);
    });

    const comment = text as string;
    const splits = comment.split(regex);

    const nodes: React.ReactNode[] = [];
    let newText = '';

    splits.forEach((textPart, index) => {
      nodes.push(textPart);
      newText += textPart;

      if (index < tags.length) {
        const tag = tags[index];
        const tagName = getNameFromMentiontag(tag);
        const mentionedUserId = getIdFromMentiontag(tag);

        newText += `@${tagName}`;

        nodes.push(
          <span
            key={mentionedUserId}
            role="button"
            tabIndex={0}
            onClick={
              showPlainText || disableMentionClick
                ? undefined
                : () => onClickMention(mentionedUserId)
            }
            style={
              showPlainText
                ? undefined
                : {
                    fontWeight: 600,
                    color: COLORS.INFO_ALT,
                    cursor: disableMentionClick ? undefined : 'pointer',
                  }
            }>
            @{tagName}
          </span>,
        );
      }
    });

    return { nodes, text: newText };
  }, [
    COLORS.INFO_ALT,
    content,
    disableMentionClick,
    onClickMention,
    showPlainText,
  ]);

  if (showPlainText) return <>{textToRender.nodes}</>;

  return <ReactLinkify>{textToRender.nodes}</ReactLinkify>;
};

export default memo(MentionedContent);
